import { NotAuthenticated } from '@feathersjs/errors';

import { globalCache } from '@sbrc/utils';

import { getApp } from '../../utils';
import { invalidateSession } from '../invalidateSession';

import { captureMustChangePin } from './mustChangePin';
import { userIDStore, type UserIDStore } from './userIDStore';

/**
 * One-time wiring of the `userIDStore` to the feathers-client auth
 * lifecycle:
 *
 * 1. `reAuthenticate()` on first access (silent restore from the JWT
 *    in localStorage, if any).
 * 2. An error hook that clears `userIDStore` on any service-level
 *    `NotAuthenticated` — `PrivatePage` observes this and navigates to
 *    `/login`.
 *
 * `globalCache` makes this idempotent: the side effects fire exactly
 * once per page, on the first call to `currentUserID()`. Subsequent
 * calls just return the store.
 *
 * Note: the wiring intentionally lives behind a *separate* cache key
 * from the store itself (which lives in `userIDStore.ts`) so that
 * callers like `invalidateSession` — which only need to write to the
 * store — can import `userIDStore` without dragging in `getApp` and
 * the import cycle that follows.
 */
/**
 * Whether an error means the box looked at this token and said no, as
 * opposed to never having answered.
 *
 * 401 is the ordinary refusal. 404 is the one a box on a build without
 * `SessionJWTStrategy` gives for a token whose subject the database no
 * longer has — the case this whole change exists for, and the reason the
 * client cannot rely on the server always phrasing it as 401.
 *
 * Everything else — a timeout, a dropped socket, a 500 — is the box failing
 * to answer. The token may well still be good, so it stays.
 */
function isTokenRefused(error: unknown): boolean {
  const code = (error as { code?: unknown } | null | undefined)?.code;

  return code === 401 || code === 403 || code === 404;
}

function wireOnce(): void {
  globalCache('currentUserID-wired', () => {
    const store = userIDStore();

    const reauth = async () => {
      try {
        const result = await getApp().reAuthenticate();
        captureMustChangePin(result.mustChangePin);
        store.set(result.user.id);
      } catch (error) {
        // A token the box *refused* must not survive the attempt. Clearing
        // the userID alone leaves it in storage, where it is not merely
        // inert: the rejected authentication promise stays parked on the
        // app, and since every service call awaits that promise, the whole
        // page keeps failing on it — including the unauthenticated poll the
        // login screen renders its passcode field behind.
        //
        // Refused is the narrow reading, matching SessionJWTStrategy's on
        // the other side of the wire. A restore restarts feathers-bot by
        // design, so loading the page mid-restart times out against a
        // socket that is not up yet — which says nothing about the token.
        // Deleting it there would charge the operator a passcode entry for
        // a refresh that used to recover on its own.
        if (
          isTokenRefused(error) &&
          localStorage.getItem('feathers-jwt') !== null
        ) {
          // Clearing the token does not un-park that promise on its own:
          // feathers resets it only for 401 and 403, never for the 404 this
          // exists for. Done here rather than in `invalidateSession`, which
          // is kept free of `getApp` to stay out of the import cycle.
          getApp().authentication.reset();
          invalidateSession(getApp());

          return;
        }

        // Nothing to end — no token, or no verdict on the one we have. A
        // cold load of the login page lands here on every visit, and ending
        // a session that never began would put a warning in the log each
        // time.
        store.set(null);
      }
    };

    reauth();

    getApp().hooks({
      error(context) {
        // listen for any NotAuthenticated errors which happen
        // when calling any service (except authentication itself)
        if (
          context.error instanceof NotAuthenticated &&
          context.path !== 'authentication' &&
          context.path !== 'controlPanelToken'
        ) {
          // Clear current user
          // Inside a <PrivatePage> this will navigate to the login page
          store.set(null);
        }

        return context;
      },
    });

    return true;
  });
}

export function currentUserID(): UserIDStore {
  wireOnce();

  return userIDStore();
}
